import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DownloadOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Collapse,
  DatePicker,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import dayjs from 'dayjs';
import { API } from '@/services/api.service';
import HowToCard from './HowToCard';
import { useGoToSection } from './howToLinks';
import FccsCodesPanel from './FccsCodesPanel';
import { formatAmount } from './format';

const { Text } = Typography;

const saveBlob = ({ blob, fileName }) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

/**
 * The FCCS upload for one company and month: what will be sent, what to look
 * at before sending it, and the Excel file itself.
 *
 * @param {Object} props - Component props
 * @param {string} props.companyId - Company whose ledger is used
 * @returns {JSX.Element} The panel
 */
export default function FccsPanel({ companyId }) {
  const { t } = useTranslation('clientReports');
  // The upload is made after the month closes, so last month is the default.
  const [period, setPeriod] = useState(
    dayjs().subtract(1, 'month').startOf('month')
  );
  const year = period.year();
  const month = period.month() + 1;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['client-reports-fccs', companyId, year, month],
    queryFn: () => API.clientReports.getFccs(companyId, { year, month }),
    retry: false,
  });
  const report = data?.data;
  const [openSections, setOpenSections] = useState([]);
  useGoToSection('fccs', (key) => setOpenSections((keys) => [...new Set([...keys, key])]));

  const exportMutation = useMutation({
    mutationFn: () => API.clientReports.exportFccs(companyId, { year, month }),
    onSuccess: saveBlob,
    onError: (e) => message.error(e?.response?.data?.message || e.message),
  });

  const columns = useMemo(
    () => [
      {
        title: t('fccs.account'),
        dataIndex: 'account',
        key: 'account',
        width: 110,
      },
      { title: t('fccs.accountName'), dataIndex: 'name', key: 'name' },
      {
        title: t('fccs.code'),
        dataIndex: 'code',
        key: 'code',
        width: 260,
        filters: [
          { text: t('fccs.withCode'), value: 'with' },
          { text: t('fccs.withoutCode'), value: 'without' },
        ],
        onFilter: (value, row) =>
          value === 'with' ? Boolean(row.code) : !row.code,
        render: (code, row) =>
          code ? (
            <Space size={4}>
              <Tag color="blue">{code}</Tag>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {row.codeName}
              </Text>
            </Space>
          ) : null,
      },
      {
        title: t('fccs.balance'),
        dataIndex: 'balance',
        key: 'balance',
        width: 180,
        align: 'right',
        render: formatAmount,
      },
    ],
    [t]
  );

  // 404 = nothing has arrived from AB for that year yet.
  const errorMessage =
    error?.response?.status === 404
      ? t('fccs.noLedger', { year })
      : error?.response?.data?.message || error?.message;

  return (
    <div>
      <HowToCard guide="fccs" open={report ? !report.configured : undefined} />
      <Space id="howto-fccs-toolbar" wrap style={{ display: 'flex', marginBottom: 12 }}>
        <DatePicker
          picker="month"
          allowClear={false}
          value={period}
          format="MM.YYYY"
          onChange={(value) => value && setPeriod(value.startOf('month'))}
        />
        <Button
          type="primary"
          className="btn-primary"
          icon={<DownloadOutlined />}
          disabled={!report?.rows?.length}
          loading={exportMutation.isPending}
          onClick={() => exportMutation.mutate()}
        >
          {t('fccs.download')}
        </Button>
      </Space>

      {errorMessage && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={errorMessage}
        />
      )}

      {report && (
        <>
          <Space size="large" style={{ display: 'flex', marginBottom: 12 }}>
            <Statistic
              title={t('fccs.rows')}
              value={(report.fileRows || report.rows).length}
            />
            <Statistic
              title={t('fccs.total')}
              value={formatAmount(report.total)}
              valueStyle={{
                color: Math.abs(report.total) >= 0.01 ? '#cf1322' : undefined,
              }}
            />
            <Statistic
              title={t('fccs.lastEntry')}
              value={
                report.sync.lastEntryDate
                  ? dayjs(report.sync.lastEntryDate).format('DD.MM.YYYY')
                  : '—'
              }
            />
          </Space>

          {!report.configured && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
              message={t('fccs.notConfigured')}
            />
          )}

          {report.warnings.map((w) => {
            if (w.type === 'unbalanced') {
              return (
                <Alert
                  key={w.type}
                  type="error"
                  showIcon
                  style={{ marginBottom: 8 }}
                  message={t('fccs.warnUnbalanced', {
                    total: formatAmount(w.total),
                  })}
                />
              );
            }
            if (w.type === 'extractedBeforeMonthEnd') {
              return (
                <Alert
                  key={w.type}
                  type="warning"
                  showIcon
                  style={{ marginBottom: 8 }}
                  message={t('fccs.warnExtractedEarly', {
                    date: dayjs(w.extractedAt).format('DD.MM.YYYY HH:mm'),
                  })}
                />
              );
            }
            if (w.type === 'uncodedRelatedParty') {
              return (
                <Alert
                  key={w.type}
                  type="warning"
                  showIcon
                  style={{ marginBottom: 8 }}
                  message={t('fccs.warnUncoded', { count: w.count })}
                  description={
                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                      {report.uncoded.map((u) => (
                        <li key={u.account}>
                          <Text strong>{u.account}</Text> {u.name} —{' '}
                          {formatAmount(u.balance)}
                          {u.partners.some((p) => p.partner) && (
                            <Text type="secondary">
                              {' '}
                              (
                              {u.partners
                                .filter((p) => p.partner)
                                .map((p) => `${p.partner} ${p.name}`.trim())
                                .join(', ')}
                              )
                            </Text>
                          )}
                        </li>
                      ))}
                    </ul>
                  }
                />
              );
            }
            return null;
          })}

          <Table
            scroll={{ x: 'max-content' }}
            rowKey={(r) => `${r.account}|${r.code}`}
            size="small"
            loading={isLoading}
            columns={columns}
            dataSource={report.rows}
            pagination={{ pageSize: 50, showSizeChanger: true }}
            expandable={{
              rowExpandable: (r) => r.partners.some((p) => p.partner),
              expandedRowRender: (r) => (
                <Table
                  scroll={{ x: 'max-content' }}
                  rowKey={(p) => p.partner || '-'}
                  size="small"
                  pagination={false}
                  dataSource={r.partners}
                  columns={[
                    {
                      title: t('fccs.partner'),
                      dataIndex: 'partner',
                      key: 'partner',
                      width: 140,
                      render: (v) =>
                        v || (
                          <Text type="secondary">{t('fccs.noPartner')}</Text>
                        ),
                    },
                    {
                      title: t('fccs.partnerName'),
                      dataIndex: 'name',
                      key: 'name',
                    },
                    {
                      title: t('fccs.balance'),
                      dataIndex: 'balance',
                      key: 'balance',
                      width: 180,
                      align: 'right',
                      render: formatAmount,
                    },
                  ]}
                />
              ),
            }}
          />
        </>
      )}

      <Collapse
        style={{ marginTop: 16 }}
        activeKey={openSections}
        onChange={setOpenSections}
        items={[
          {
            key: 'codes',
            label: t('fccs.codesTitle'),
            children: (
              <div id="howto-fccs-codes">
                <FccsCodesPanel companyId={companyId} onSaved={refetch} />
              </div>
            ),
          },
        ]}
      />
    </div>
  );
}
